import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Input, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AppBar } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  CenterSpinner,
  EmptyText,
  Png,
  SelectSheet,
  useSheet,
} from "../../components/managementUi";
import ConfirmDialog from "../../components/ConfirmDialog";
import { appImages } from "../../assets/appicons";
import {
  getSignatureListApi,
  deleteSignatureApi,
  downloadDraftSignatureApi,
} from "../../api/signature.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";
import {
  ALL_FILTER_VALUE,
  DEFAULT_SIGNATURE_LIMIT,
  SIGNATURE_SOURCE_TYPE,
  SIGNATURE_STATUS,
  SIGNATURE_STATUS_FILTER_OPTIONS,
  SIGNATURE_TYPE_FILTER_OPTIONS,
  getSourceTypeLabel,
  getStatusLabel,
} from "./signature.constant";

// ===== Màu lấy từ mobile-app-v2 (src/themes/colors/default.ts) =====
const C = {
  primary200: "#4081EB",
  base100: "#FFFFFF",
  base200: "#E1E1E1",
  base600: "#C8C8C8",
  base1000: "#F9FAFD",
  baseContent: "#000000",
  subTitle: "#51607B",
  error: "#EC1515",
  errorBanner: "#FFEDEB",
  successFocus: "#00AF83",
  successBanner: "#E5FFEF",
  blue50: "#EFF8FF",
  blue600: "#1570EF",
  orange50: "#FFF6ED",
  orange600: "#EC4A0A",
};

/* --------------------------------------------------- ô lọc (khớp app) */
const FilterField = ({ label, value, onClick }) => (
  <Box as="button" type="button" onClick={onClick} flex="1" minW="0" textAlign="left">
    <Text fontSize="14px" fontWeight={500} color={C.baseContent}>
      {label}
    </Text>
    <Box h="6px" />
    <Flex
      align="center"
      justify="space-between"
      border="1px solid"
      borderColor={C.base200}
      bg={C.base100}
      px="12px"
      h="40px"
      borderRadius="8px"
    >
      <Text fontSize="14px" fontWeight={500} color={C.baseContent} noOfLines={1} flex="1">
        {value}
      </Text>
      <Png src={appImages.icon_chevron_down} size={16} color={C.base600} />
    </Flex>
  </Box>
);

/* ------------------------------------------------ nhãn loại / trạng thái */
const Tag = ({ label, bg, color }) => (
  <Box bg={bg} borderRadius="6px" px="8px" py="3px">
    <Text fontSize="12px" fontWeight={600} color={color}>
      {label}
    </Text>
  </Box>
);

/* ---------------------------------------------------- thẻ 1 chữ kí (app) */
const SignatureCard = ({ signature, onDraft, onDelete }) => {
  const isDrawn = signature.sourceType === SIGNATURE_SOURCE_TYPE.DRAWN;
  const isEnable = signature.status === SIGNATURE_STATUS.ENABLE;

  return (
    <Box
      border="1px solid"
      borderColor={C.base200}
      borderRadius="8px"
      bg={C.base100}
      p="12px"
    >
      {/* Hành động: kí nháp + xóa */}
      <Flex justify="flex-end" align="center" gap="8px" mb="6px">
        <Box as="button" type="button" title="Kí nháp" onClick={() => onDraft(signature)}>
          <Png src={appImages.icon_edit} size={18} color={C.primary200} />
        </Box>
        <Box as="button" type="button" title="Xóa" onClick={() => onDelete(signature)}>
          <Png src={appImages.icon_delete_square} size={22} color={C.error} />
        </Box>
      </Flex>

      {/* Ảnh chữ kí */}
      <Flex
        w="100%"
        sx={{ aspectRatio: "1 / 1" }}
        border="1px solid"
        borderColor={C.base200}
        borderRadius="6px"
        bg={C.base1000}
        align="center"
        justify="center"
        overflow="hidden"
        mb="8px"
      >
        {signature.signatureImageBase64 ? (
          <img
            src={signature.signatureImageBase64}
            alt={signature.title}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <Text fontSize="13px" color={C.subTitle}>
            Đang tải chữ kí...
          </Text>
        )}
      </Flex>

      {/* Tiêu đề */}
      <Text fontSize="14px" fontWeight={600} color={C.baseContent} noOfLines={1} mb="6px">
        {signature.title}
      </Text>

      {/* Nhãn loại + trạng thái */}
      <Flex flexWrap="wrap" gap="6px">
        <Tag
          label={getSourceTypeLabel(signature.sourceType)}
          bg={isDrawn ? C.blue50 : C.orange50}
          color={isDrawn ? C.blue600 : C.orange600}
        />
        <Tag
          label={getStatusLabel(signature.status)}
          bg={isEnable ? C.successBanner : C.errorBanner}
          color={isEnable ? C.successFocus : C.error}
        />
      </Flex>
    </Box>
  );
};

/* ============================================================== màn hình */
const SignatureList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sheet = useSheet(); // "TYPE" | "STATUS"

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL_FILTER_VALUE);
  const [statusFilter, setStatusFilter] = useState(ALL_FILTER_VALUE);
  // Chữ kí đang chờ xác nhận xóa (khớp Alert.alert "Xóa chữ kí" của app)
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Debounce ô tìm kiếm (khớp app: 400ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["emp", "signature-list", typeFilter, statusFilter, debouncedSearch],
    queryFn: () =>
      getSignatureListApi({
        page: 1,
        limit: DEFAULT_SIGNATURE_LIMIT,
        ...(typeFilter !== ALL_FILTER_VALUE ? { sourceType: typeFilter } : {}),
        ...(statusFilter !== ALL_FILTER_VALUE ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
  });

  const signatures = data?.signatures || [];

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteSignatureApi(id),
    onSuccess: () => {
      toast.success("Xóa chữ kí thành công");
      queryClient.invalidateQueries({ queryKey: ["emp", "signature-list"] });
      setDeleteTarget(null);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e, "Xóa chữ kí thất bại"));
      setDeleteTarget(null);
    },
  });

  // Kí nháp: tải file docx mẫu đã chèn chữ kí về máy
  const draftMutation = useMutation({
    mutationFn: (signature) => downloadDraftSignatureApi(signature._id),
    onSuccess: (blob, signature) => {
      const safeName = (signature.title || `chu_ky_${signature._id}`)
        .replace(/[^\p{L}\p{N}_-]+/gu, "_")
        .slice(0, 60);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ki_nhap_${safeName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Không mở được file kí nháp")),
  });

  const typeLabel = useMemo(
    () =>
      SIGNATURE_TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label ??
      SIGNATURE_TYPE_FILTER_OPTIONS[0].label,
    [typeFilter]
  );

  const statusLabel = useMemo(
    () =>
      SIGNATURE_STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ??
      SIGNATURE_STATUS_FILTER_OPTIONS[0].label,
    [statusFilter]
  );

  const isTypeSheet = sheet.type === "TYPE";
  const sheetOptions = isTypeSheet
    ? SIGNATURE_TYPE_FILTER_OPTIONS
    : SIGNATURE_STATUS_FILTER_OPTIONS;

  const pageBody = (
    <>

      <Box px="16px" pt="8px">
        <Text fontSize="14px" color={C.baseContent}>
          Quản lý chữ kí cá nhân của bạn
        </Text>

        <Box h="12px" />

        {/* Ô tìm kiếm */}
        <Flex
          align="center"
          border="1px solid"
          borderColor={C.base200}
          borderRadius="8px"
          px="12px"
          h="44px"
        >
          <Png src={appImages.icon_search} size={18} color={C.base600} />
          <Input
            variant="unstyled"
            ml="8px"
            fontSize="14px"
            color={C.baseContent}
            placeholder="Tìm kiếm theo tiêu đề..."
            _placeholder={{ color: C.base600 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Flex>

        <Box h="12px" />

        {/* Bộ lọc loại + trạng thái */}
        <Flex gap="12px">
          <FilterField label="Loại chữ kí" value={typeLabel} onClick={() => sheet.open("TYPE")} />
          <FilterField label="Trạng thái" value={statusLabel} onClick={() => sheet.open("STATUS")} />
        </Flex>

        <Box h="12px" />

        {/* Nút tạo chữ kí số */}
        <Flex
          as="button"
          type="button"
          onClick={() => navigate(`${EMP_BASE_PATH}/chu-ky/tao`)}
          w="100%"
          h="44px"
          borderRadius="8px"
          bg={C.primary200}
          align="center"
          justify="center"
          gap="8px"
        >
          <Png src={appImages.icon_plus} size={16} color={C.base100} />
          <Text fontSize="15px" fontWeight={700} color={C.base100}>
            Tạo chữ kí số
          </Text>
        </Flex>

        <Box h="12px" />
      </Box>

      {isLoading ? (
        <CenterSpinner />
      ) : signatures.length === 0 ? (
        <EmptyText text="Không có chữ kí phù hợp" />
      ) : (
        <Box
          px="16px"
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap="12px"
        >
          {signatures.map((item) => (
            <SignatureCard
              key={item._id}
              signature={item}
              onDraft={(s) => draftMutation.mutate(s)}
              onDelete={setDeleteTarget}
            />
          ))}
        </Box>
      )}

      <SelectSheet
        isOpen={sheet.isOpen}
        onClose={sheet.close}
        title={isTypeSheet ? "Loại chữ kí" : "Trạng thái"}
        data={sheetOptions.map((o) => ({ key: o.value, label: o.label, raw: o.value }))}
        selectedKey={isTypeSheet ? typeFilter : statusFilter}
        onSelect={(raw) => {
          if (isTypeSheet) setTypeFilter(raw);
          else setStatusFilter(raw);
          sheet.close();
        }}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xóa chữ kí"
        description={`Bạn có chắc chắn muốn xóa chữ kí "${deleteTarget?.title || ""}" không?`}
        confirmText="Xóa"
        cancelText="Hủy"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Thông tin chữ kí" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Thông tin chữ kí" />
      {pageBody}
    </Box>
  );
};

export default SignatureList;
